"use client";

import { useEffect, useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY } from "@/components/tool-kit/styles";
import { PlaceFields } from "@/components/tools/astronomy/place-fields";
import { SunAngleResults } from "@/components/tools/astronomy/sun-angle-results";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  type PlaceFieldValues,
  readPlace,
} from "@/lib/tool-kit/astronomy/place-input";
import {
  nowInZone,
  parseClock,
  parseIsoDay,
  zonedToInstant,
} from "@/lib/tool-kit/astronomy/zone";

/** English strings. A translated content file overrides these through `ui`. */
export const SUN_ANGLE_CALCULATOR_STRINGS = {
  place: "Place",
  custom: "Custom coordinates",
  placeNames: {
    "new-york": "New York, USA",
    chicago: "Chicago, USA",
    denver: "Denver, USA",
    "los-angeles": "Los Angeles, USA",
    anchorage: "Anchorage, USA",
    honolulu: "Honolulu, USA",
    "mexico-city": "Mexico City, Mexico",
    quito: "Quito, Ecuador",
    "sao-paulo": "São Paulo, Brazil",
    "buenos-aires": "Buenos Aires, Argentina",
    reykjavik: "Reykjavik, Iceland",
    london: "London, UK",
    paris: "Paris, France",
    berlin: "Berlin, Germany",
    tromso: "Tromsø, Norway",
    moscow: "Moscow, Russia",
    cairo: "Cairo, Egypt",
    lagos: "Lagos, Nigeria",
    nairobi: "Nairobi, Kenya",
    johannesburg: "Johannesburg, South Africa",
    dubai: "Dubai, UAE",
    mumbai: "Mumbai, India",
    singapore: "Singapore",
    beijing: "Beijing, China",
    tokyo: "Tokyo, Japan",
    sydney: "Sydney, Australia",
    auckland: "Auckland, New Zealand",
  },
  latitude: "Latitude (° north, − for south)",
  longitude: "Longitude (° east, − for west)",
  latitudeHint: "Decimal degrees, for example 40.7128 or −33.87.",
  zone: "Time zone",
  locate: "Use my location",
  locating: "Locating…",
  locateFailed:
    "Your browser could not share a location. Pick a city or type coordinates instead.",
  date: "Date",
  time: "Local time",
  now: "Set to now",
  copy: "Copy result",
  copied: "Copied!",
  results: "The Sun at this moment",
  elevation: "Elevation above the horizon",
  azimuth: "Azimuth (clockwise from north)",
  zenith: "Zenith angle",
  geometric: "Elevation without refraction",
  declination: "Solar declination",
  equationOfTime: "Equation of time",
  hourAngle: "Hour angle",
  shadow: "Shadow of a 1 m pole",
  noShadow: "None (Sun below horizon)",
  metres: "{value} m",
  compass: [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ],
  phase: {
    day: "Daylight: the Sun is up.",
    civil: "Civil twilight: the Sun is up to 6° below the horizon.",
    nautical: "Nautical twilight: the Sun is 6° to 12° below the horizon.",
    astronomical:
      "Astronomical twilight: the Sun is 12° to 18° below the horizon.",
    night: "Night: the Sun is more than 18° below the horizon.",
  },
  dayHeading: "The Sun on {date}",
  sunrise: "Sunrise",
  solarNoon: "Solar noon",
  sunset: "Sunset",
  noonElevation: "Elevation at solar noon",
  daylight: "Daylight",
  duration: "{hours} h {minutes} min",
  nextDay: "{time} (next day)",
  previousDay: "{time} (previous day)",
  polarDay: "Polar day: the Sun does not set on this date at this latitude.",
  polarNight:
    "Polar night: the Sun does not rise on this date at this latitude.",
  chartHeading: "The Sun's path through the day",
  chartLabel: "Solar elevation through the day",
  chartDescription:
    "A line chart of the Sun's elevation in degrees against the local clock time from midnight to midnight, with a marker at the time you entered. The line is below zero at night and peaks at solar noon.",
  chartX: "Local time (hours)",
  chartY: "Elevation (°)",
  workingHeading: "The working",
  workingJulian:
    "Julian day {jd} (UTC {utc}). The NOAA algorithm turns that into the Sun's declination δ = {declination}° and the equation of time {eot} min.",
  workingSolarTime:
    "True solar time = UTC minutes + equation of time + 4 × longitude = {tst}, so the hour angle is H = {ha}°.",
  workingElevation:
    "sin(elevation) = sin φ sin δ + cos φ cos δ cos H, with φ = {lat}°. That gives {geo}° geometrically; refraction adds {refraction}°, for {elevation}°.",
  invalidPlace:
    "Enter a latitude between −90 and 90 and a longitude between −180 and 180.",
  invalidTime: "Enter a valid date and time.",
  accuracy:
    "NOAA's equations are accurate to about a minute for sunrise and sunset between 72° north and south, and to ten minutes beyond. Real refraction depends on air pressure and temperature, so treat low-Sun elevations as approximate.",
  summary:
    "{place}, {date} {time} ({zone}): elevation {elevation}°, azimuth {azimuth}° ({compass}).",
} as const;

export type SunAngleStrings = typeof SUN_ANGLE_CALCULATOR_STRINGS;

interface Form extends PlaceFieldValues {
  date: string;
  time: string;
}

const INITIAL: Form = {
  place: "new-york",
  latitude: "40.7128",
  longitude: "-74.006",
  zone: "America/New_York",
  date: "2026-06-21",
  time: "12:00",
};

export default function SunAngleCalculatorWidget() {
  const strings = useToolUi(SUN_ANGLE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const [form, setForm] = useState<Form>(INITIAL);

  // Start from the current moment, once the page is on the reader's machine.
  useEffect(() => {
    setForm((current) => ({ ...current, ...nowInZone(current.zone) }));
  }, []);

  const patch = (next: Partial<Form>) =>
    setForm((current) => ({ ...current, ...next }));
  const place = readPlace(form);
  const day = parseIsoDay(form.date);
  const clock = parseClock(form.time);
  const instant =
    day === null || clock === null
      ? null
      : zonedToInstant({ ...day, ...clock }, form.zone);
  const ready = place.ok && day !== null && instant !== null;

  return (
    <div className="space-y-6">
      <PlaceFields
        labels={strings}
        onChange={patch}
        prefix="sa"
        values={form}
      />
      <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <TextField
          id="sa-date"
          label={strings.date}
          onChange={(date) => patch({ date })}
          type="date"
          value={form.date}
        />
        <TextField
          id="sa-time"
          label={strings.time}
          onChange={(time) => patch({ time })}
          type="time"
          value={form.time}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => patch(nowInZone(form.zone))}
          type="button"
        >
          {strings.now}
        </button>
      </div>
      <div aria-live="polite" className="space-y-6">
        {place.ok ? null : <ErrorNotice>{strings.invalidPlace}</ErrorNotice>}
        {place.ok && !ready ? (
          <ErrorNotice>{strings.invalidTime}</ErrorNotice>
        ) : null}
        {ready && place.ok && instant !== null ? (
          <SunAngleResults
            form={form}
            instant={instant}
            latitude={place.latitude}
            locale={locale}
            longitude={place.longitude}
            strings={strings}
          />
        ) : null}
      </div>
      <Notice>{strings.accuracy}</Notice>
    </div>
  );
}
